// Timezone Settings Module
// Handles timezone configuration UI and service integration


/**
 * TimezoneSettings class manages the timezone configuration UI
 *
 * Features:
 * - Loads current timezone from service on initialization
 * - Handles timezone selector changes
 * - Updates timezone configuration in centralized service
 * - Refreshes all consumer caches after changes
 *
 * Architecture:
 * - Follows SOA principles by consuming centralized Timezone Configuration Service
 * - Coordinates cache refresh across multiple consumers (discovery logger, ii_agent)
 * - Provides visual feedback during updates
 *
 * @class TimezoneSettings
 */
export class TimezoneSettings {
    private selector: HTMLSelectElement | null;
    private statusDiv: HTMLElement | null;
    private currentLabel: HTMLElement | null;

    constructor() {
        this.selector = null;
        this.statusDiv = null;
        this.currentLabel = null;
    }

    /**
     * Initializes the timezone settings UI
     * Sets up event listeners and loads current configuration
     */
    public async initialize(): Promise<void> {
        console.log('🕐 Initializing timezone settings...');

        // Get DOM elements
        this.selector = document.getElementById('timezone-selector') as HTMLSelectElement;
        this.statusDiv = document.getElementById('timezone-status');
        this.currentLabel = document.getElementById('timezone-current-label');

        if (!this.selector) {
            console.warn('⚠️ Timezone selector not found');
            return;
        }

        // Load current timezone configuration
        await this.loadCurrentTimezone();

        // Set up change handler
        this.selector.addEventListener('change', () => this.handleTimezoneChange());

        console.log('✓ Timezone settings initialized');
    }

    /**
     * Loads current timezone configuration from service
     * Updates the UI to reflect current settings
     * @private
     */
    private async loadCurrentTimezone(): Promise<void> {
        try {
            if (!window.canisterClient) {
                console.warn('⚠️ CanisterClient not available');
                return;
            }

            const response = await window.canisterClient.getTimezoneConfig();
            console.log('🔍 Raw timezone response:', response);

            // Parse from raw Candid string if needed
            let config;
            // Check if data field has the required properties, otherwise parse from raw
            if (response.data && response.data.utc_offset_hours !== undefined && response.data.label) {
                config = response.data;
                console.log('✓ Using data field:', config);
            } else if (response.raw) {
                const offsetMatch = response.raw.match(/utc_offset_hours\s*=\s*(-?\d+)/);
                const labelMatch = response.raw.match(/label\s*=\s*"([^"]+)"/);
                const descMatch = response.raw.match(/description\s*=\s*"([^"]+)"/);

                if (offsetMatch && labelMatch) {
                    config = {
                        utc_offset_hours: parseInt(offsetMatch[1], 10),
                        label: labelMatch[1],
                        description: descMatch ? descMatch[1] : `${labelMatch[1]} timezone`
                    };
                    console.log('✓ Parsed timezone from Candid:', config);
                } else {
                    throw new Error('Failed to parse timezone from Candid');
                }
            } else {
                config = response;
                console.log('✓ Using response directly:', config);
            }

            // Update selector to match current config
            if (this.selector) {
                const value = `${config.utc_offset_hours}:${config.label}`;
                console.log(`🔍 Setting selector value to: "${value}"`);
                this.selector.value = value;
                console.log(`🔍 Selector value after set: "${this.selector.value}"`);

                // If value doesn't exist in options, add it
                if (this.selector.value !== value) {
                    console.warn(`⚠️ Value "${value}" not found in options, adding it`);
                    const option = document.createElement('option');
                    option.value = value;
                    option.textContent = `${config.label} - ${config.description}`;
                    option.selected = true;
                    this.selector.appendChild(option);
                }
            } else {
                console.warn('⚠️ Selector element not found');
            }

            // Show current timezone status
            const offset = config.utc_offset_hours;
            console.log(`🔍 Displaying status - offset: ${offset}, label: ${config.label}`);
            const sign = offset >= 0 ? '+' : '';
            this.updateStatus(`Current timezone: ${config.label} (UTC${sign}${offset})`, false);

        } catch (error: any) {
            console.error('❌ Failed to load current timezone:', error);
            this.updateStatus('Failed to load timezone configuration', true);
        }
    }

    /**
     * Handles timezone selector change events
     * Updates the timezone configuration service and refreshes all caches
     * @private
     */
    private async handleTimezoneChange(): Promise<void> {
        if (!this.selector) return;

        const value = this.selector.value;
        const [offsetStr, label] = value.split(':');
        const utcOffsetHours = parseInt(offsetStr, 10);

        // Get the full option text for description
        const selectedOption = this.selector.options[this.selector.selectedIndex];
        const description = selectedOption.textContent || `${label} (UTC${utcOffsetHours >= 0 ? '+' : ''}${utcOffsetHours})`;

        console.log(`🕐 Changing timezone to: ${label} (UTC${utcOffsetHours >= 0 ? '+' : ''}${utcOffsetHours})`);

        try {
            // Show loading state
            this.updateStatus('Updating timezone...', false);
            if (this.selector) {
                this.selector.disabled = true;
            }

            // Update timezone configuration in service
            if (!window.canisterClient) {
                throw new Error('CanisterClient not available');
            }

            const result = await window.canisterClient.setTimezoneConfig(
                utcOffsetHours,
                label,
                description
            );

            // Check if update was successful
            if ('err' in result) {
                throw new Error(result.err);
            }

            console.log('✓ Timezone updated successfully');

            // Refresh caches in all consumers
            await this.refreshAllCaches();

            // Show success message
            this.updateStatus(`✓ Timezone updated to ${label} (UTC${utcOffsetHours >= 0 ? '+' : ''}${utcOffsetHours})`, false);

            // Log to discovery logger if available
            if (window.appCore?.getModule('discoveryLogger')) {
                const logger = window.appCore.getModule('discoveryLogger');
                logger?.logSuccess(`System timezone changed to ${label} (UTC${utcOffsetHours >= 0 ? '+' : ''}${utcOffsetHours})`);
            }

        } catch (error: any) {
            console.error('❌ Failed to update timezone:', error);
            this.updateStatus(`❌ Failed to update: ${error.message}`, true);

            // Reload current timezone to reset selector
            await this.loadCurrentTimezone();
        } finally {
            // Re-enable selector
            if (this.selector) {
                this.selector.disabled = false;
            }
        }
    }

    /**
     * Refreshes timezone caches in all consumers
     * Ensures all logging components use the new timezone immediately
     * @private
     */
    private async refreshAllCaches(): Promise<void> {
        console.log('🔄 Refreshing timezone caches across all consumers...');

        // Refresh discovery logger cache
        if (window.discoveryLogger) {
            try {
                if (typeof window.discoveryLogger.refreshTimezoneConfig === 'function') {
                    await window.discoveryLogger.refreshTimezoneConfig();
                    console.log('✓ Discovery logger cache refreshed');
                }
            } catch (error: any) {
                console.warn('⚠️ Failed to refresh discovery logger cache:', error);
            }
        }

        // Refresh ii_agent cache (for service map log timestamps)
        if (window.canisterClient) {
            try {
                console.log('🔄 Refreshing ii_agent timezone cache...');
                const result = await window.canisterClient.callCanister(
                    'ii_agent',
                    'refresh_timezone_config',
                    []
                );
                console.log('✓ II Agent timezone cache refreshed:', result);
            } catch (error: any) {
                console.warn('⚠️ Failed to refresh ii_agent cache:', error);
            }
        }

        console.log('✓ All caches refreshed');
    }

    /**
     * Updates the status message display
     * @param message - The status message to display
     * @param isError - Whether this is an error message
     * @private
     */
    private updateStatus(message: string, isError: boolean): void {
        if (!this.statusDiv || !this.currentLabel) return;

        this.currentLabel.textContent = message;
        this.statusDiv.classList.remove('hidden');

        if (isError) {
            this.statusDiv.classList.add('text-red-600');
            this.statusDiv.classList.remove('text-green-600', 'text-gray-600');
        } else if (message.startsWith('✓')) {
            this.statusDiv.classList.add('text-green-600');
            this.statusDiv.classList.remove('text-red-600', 'text-gray-600');
        } else {
            this.statusDiv.classList.add('text-gray-600');
            this.statusDiv.classList.remove('text-red-600', 'text-green-600');
        }
    }
}

// Export for global access
declare global {
    interface Window {
        TimezoneSettings: typeof TimezoneSettings;
        timezoneSettings?: TimezoneSettings;
    }
}

window.TimezoneSettings = TimezoneSettings;

// Auto-initialize when page loads
if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => {
        window.timezoneSettings = new TimezoneSettings();
        // Delay initialization to ensure canisterClient is ready
        setTimeout(() => {
            window.timezoneSettings?.initialize();
        }, 1000);
    });
}
